import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { useUiStore } from '@/store/uiStore';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an unhandled error:', error, errorInfo);
  }

  private handleReset = () => {
    try {
      useSimulationStore.getState().stop();
      useSimulationStore.getState().reset();
      useUiStore.getState().clearSelection();
      useProjectStore.getState().newProject();
    } catch (e) {
      console.error('Error while resetting project in ErrorBoundary:', e);
    }
    this.setState({ hasError: false, error: null });
  };

  public override render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            backgroundColor: 'var(--color-bg-base, #0f172a)',
            color: 'var(--color-text, #f8fafc)',
            padding: 32,
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: 520,
              backgroundColor: 'var(--color-bg-elevated, #1e293b)',
              padding: '32px 40px',
              borderRadius: 12,
              border: '1px solid var(--color-danger, #ef4444)',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 8px 0', color: 'var(--color-danger, #ef4444)' }}>
              {this.props.fallbackTitle || 'Devre Yüklenirken Bir Sorun Oluştu'}
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted, #94a3b8)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
              Yüklenen dosya biçimi eksik veya uyumsuz öğeler içeriyor olabilir. Aşağıdaki butonla yeni bir projeye dönebilir veya sayfayı yenileyebilirsiniz.
            </p>
            {this.state.error?.message && (
              <pre
                style={{
                  fontSize: '0.75rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  padding: '8px 12px',
                  borderRadius: 6,
                  color: '#f87171',
                  overflowX: 'auto',
                  marginBottom: 20,
                  textAlign: 'left',
                }}
              >
                {this.state.error.message}
              </pre>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Button variant="primary" onClick={this.handleReset}>
                Yeni Projeye Sıfırla
              </Button>
              <Button variant="secondary" onClick={() => window.location.reload()}>
                Sayfayı Yenile
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
